/**
 * MobileFeeEditorPage
 *
 * Route-based wrapper for the MobileFeeEditor component.
 * Route: /settings/portfolio/reference-data/brokers/:brokerId/fees/:nodeId
 *
 * Validates URL params (brokerId, nodeId), syncs broker context,
 * creates local dirty refs, and renders MobileFeeEditor.
 * The nodeId param is URL-encoded on navigation (encodeURIComponent) and
 * auto-decoded by React Router on read (useParams).
 *
 * Features: param validation, node existence check, MobileFeeEditor rendering,
 * navigation guard with unsaved-changes modal (Cancel/Discard/Save),
 * page-level Save/Discard CardFooter via EditorUiState bridge.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save } from 'lucide-react';

// 2. Workspace packages
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Breadcrumbs,
  BreadcrumbItem,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToastNotification,
} from '@enfomentis/ui';

import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';

// 3. Relative — domain context, components, and utilities
import { useBrokers } from '../Brokers/context';
import { buildFeeTree } from '../Brokers/feeTree/buildFeeTree';
import type { FeeTreeNode } from '../Brokers/feeTree';
import { MobileFeeEditor } from './MobileFeeEditor';
import type { EditorUiState } from './MobileFeeEditor';

// =============================================================================
// Route Constants
// =============================================================================

const SETTINGS_ROUTE = '/settings';
const PORTFOLIO_ROUTE = '/settings/portfolio';
const BROKER_LIST_ROUTE = '/settings/portfolio/reference-data/brokers';

// =============================================================================
// Helpers
// =============================================================================

/** Collect all nodes from a fee tree into a Map (for node existence validation). */
function collectNodes(nodes: FeeTreeNode[]): Map<string, FeeTreeNode> {
  const map = new Map<string, FeeTreeNode>();
  const walk = (items: FeeTreeNode[]): void => {
    for (const node of items) {
      map.set(node.id, node);
      walk(node.children);
    }
  };
  walk(nodes);
  return map;
}

// =============================================================================
// Component
// =============================================================================

export const MobileFeeEditorPage: React.FC = () => {
  const { brokerId, nodeId } = useParams<{ brokerId: string; nodeId: string }>();
  const navigate = useNavigate();

  // Context
  const {
    brokers,
    selectedBroker,
    selectedBrokerId,
    loading,
    showArchived,
    setShowArchived,
    selectBroker,
  } = useBrokers();

  // ===========================================================================
  // URL Param Validation & Context Sync
  // ===========================================================================

  useEffect(() => {
    if (loading) return;

    if (!brokerId) {
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    if (!nodeId) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees`, { replace: true });
      return;
    }

    const broker = brokers.find((b) => b.id === brokerId);

    if (!broker) {
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    if (broker.status === 'archived' && !showArchived) {
      setShowArchived(true);
      return;
    }

    if (selectedBrokerId !== brokerId) {
      selectBroker(brokerId);
    }
  }, [brokerId, nodeId, brokers, loading, selectedBrokerId, showArchived, setShowArchived, selectBroker, navigate]);

  // ===========================================================================
  // Node Existence Validation
  // ===========================================================================

  const displayMaps = useMemo(
    () => ({
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    }),
    []
  );

  const nodeMap = useMemo((): Map<string, FeeTreeNode> => {
    const configs = selectedBroker?.feeConfigurations ?? [];
    if (configs.length === 0) return new Map<string, FeeTreeNode>();
    const tree = buildFeeTree(configs, displayMaps);
    return collectNodes(tree);
  }, [selectedBroker?.feeConfigurations, displayMaps]);

  // Redirect if nodeId does not exist in the fee tree (after broker loads)
  useEffect(() => {
    if (loading || !selectedBroker || selectedBroker.id !== brokerId) return;
    if (!nodeId) return;

    if (!nodeMap.has(nodeId)) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees`, { replace: true });
    }
  }, [loading, selectedBroker, brokerId, nodeId, nodeMap, navigate]);

  // ===========================================================================
  // Local Dirty Refs (editor manages its own dirty state on the route page)
  // ===========================================================================

  const isDirtyRef = useRef(false);
  const discardRef = useRef<(() => void) | null>(null);
  const saveRef = useRef<(() => Promise<boolean>) | null>(null);

  // ===========================================================================
  // Navigation Guard State
  // ===========================================================================

  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [isNavSaving, setIsNavSaving] = useState(false);

  // ===========================================================================
  // Editor State Bridge (reactive state for page-level Save/Discard footer)
  // ===========================================================================

  const { showToast } = useToastNotification();

  const [editorState, setEditorState] = useState<EditorUiState>({
    isDirty: false,
    isSaving: false,
    hasActiveConfig: false,
  });

  // Reset bridge state on route changes to avoid stale footer flash
  useEffect(() => {
    setEditorState({ isDirty: false, isSaving: false, hasActiveConfig: false });
  }, [brokerId, nodeId]);

  const handleEditorStateChange = useCallback(
    (state: EditorUiState): void => {
      setEditorState(state);
    },
    []
  );

  /** Check dirty ref and either navigate immediately or show unsaved-changes modal. */
  const guardedNavigate = useCallback(
    (navFn: () => void): void => {
      if (isDirtyRef.current) {
        // Wrap in updater form to avoid React treating navFn as a state updater
        setPendingNavigation(() => navFn);
      } else {
        navFn();
      }
    },
    [isDirtyRef]
  );

  // ===========================================================================
  // Node Select Handler (navigates between routes)
  // ===========================================================================

  const handleNodeSelect = useCallback(
    (targetNodeId: string | null): void => {
      const navFn = (): void => {
        if (targetNodeId === null) {
          navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees`);
        } else {
          navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees/${encodeURIComponent(targetNodeId)}`);
        }
      };
      guardedNavigate(navFn);
    },
    [brokerId, navigate, guardedNavigate]
  );

  // ===========================================================================
  // Loading / Not-Found Guard
  // ===========================================================================

  if (!selectedBroker || selectedBroker.id !== brokerId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <>
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={() => guardedNavigate(() => navigate(SETTINGS_ROUTE))}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={() => guardedNavigate(() => navigate(PORTFOLIO_ROUTE))}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Portfolio
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={() => guardedNavigate(() => navigate(BROKER_LIST_ROUTE))}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Brokers
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={() => guardedNavigate(() => navigate(`${BROKER_LIST_ROUTE}/${brokerId}`))}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              {selectedBroker.name}
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={() => guardedNavigate(() => navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees`))}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Fee Configurations
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
      </CardHeader>

      {/* Content — MobileFeeEditor */}
      <CardBody className="flex-1 flex flex-col min-h-0 p-0">
        {nodeId && (
          <MobileFeeEditor
            selectedNodeId={nodeId}
            feeConfigurations={selectedBroker.feeConfigurations}
            onNodeSelect={handleNodeSelect}
            isDirtyRef={isDirtyRef}
            discardRef={discardRef}
            saveRef={saveRef}
            onEditorStateChange={handleEditorStateChange}
          />
        )}
      </CardBody>

      {/* Page-level Save/Discard Footer (driven by editor state bridge) */}
      {editorState.hasActiveConfig && (
        <CardFooter className={`shrink-0 flex items-center justify-between px-3 py-2 ${editorState.isDirty ? 'border-t-2 border-warning-300 bg-warning-50' : 'border-t border-default-200'}`}>
          <div>
            {editorState.isDirty && (
              <span className="text-sm text-warning-600">
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="flat"
              size="sm"
              isDisabled={!editorState.isDirty || editorState.isSaving}
              onPress={() => discardRef.current?.()}
            >
              Discard
            </Button>
            <Button
              color="primary"
              size="sm"
              isDisabled={!editorState.isDirty || editorState.isSaving}
              isLoading={editorState.isSaving}
              onPress={() => {
                if (!saveRef.current) {
                  showToast({ title: 'Unable to save — no active editor', color: 'warning' });
                  return;
                }
                void saveRef.current();
              }}
              startContent={!editorState.isSaving ? <Save size={14} /> : undefined}
            >
              Save
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>

    {/* Unsaved Changes Navigation Modal */}
    <Modal
      isOpen={!!pendingNavigation}
      onOpenChange={(open) => !open && !isNavSaving && setPendingNavigation(null)}
      isDismissable={!isNavSaving}
      hideCloseButton={isNavSaving}
      size="sm"
      placement="center"
    >
      <ModalContent>
        <ModalHeader>Unsaved Changes</ModalHeader>
        <ModalBody>
          <p className="text-text-secondary">
            You have unsaved changes. What would you like to do?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={() => setPendingNavigation(null)} isDisabled={isNavSaving}>
            Cancel
          </Button>
          <Button
            variant="flat"
            onPress={() => {
              discardRef.current?.();
              const nav = pendingNavigation;
              setPendingNavigation(null);
              nav?.();
            }}
            isDisabled={isNavSaving}
          >
            Discard
          </Button>
          <Button
            color="primary"
            isLoading={isNavSaving}
            onPress={async () => {
              if (!saveRef.current) {
                showToast({ title: 'Unable to save — no active editor', color: 'warning' });
                return;
              }
              setIsNavSaving(true);
              try {
                const success = await saveRef.current();
                if (success) {
                  const nav = pendingNavigation;
                  setPendingNavigation(null);
                  nav?.();
                }
                // On failure: keep modal open (only clear isNavSaving) so user can retry
              } finally {
                setIsNavSaving(false);
              }
            }}
            startContent={!isNavSaving ? <Save size={14} /> : undefined}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
};
